// The SUBSTITUTES list under the chord editor (plan §8, entry point 1): for
// the selected chord, every candidate the reharm engine offers, grouped by
// rung, each with its rationale and a melody-fit mark; Apply writes it into
// the active layer (creating one when the Original is active, so a
// suggestion never overwrites the original). No new surface — it lives where
// the chord is already being edited.

import { useMemo } from 'react';
import {
  REHARM_MOVES,
  suggestForChord,
  type MelodyFit,
  type ReharmCandidate,
} from '@/music/reharm';
import { findChord } from '@/music/score/formEdit';
import { activeReharmLayer, dropReharmPreview } from '@/music/score/reharm';
import { useStore } from '@/state/store';
import { useFlag } from '@/auth/flags';

const FIT_LABEL: Record<MelodyFit, string> = {
  'chord-tone': 'melody note is a chord tone',
  tension: 'melody note is a tension',
  'no-melody': 'no melody',
  none: 'clashes with the melody',
};

/** A multi-chord candidate says how many chords it writes, in words. */
const COUNT_WORDS: Record<number, string> = {
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
};

const RUNG_NAMES: Record<number, string> = {
  0: 'Colour',
  1: 'Same key',
  2: 'Dominants',
  3: 'Passing',
  4: 'Borrowed',
  5: 'Bass line',
  6: 'Coltrane',
  7: 'Modern',
};

export function ReharmSuggestions({ chordId }: { chordId: string }) {
  const score = useStore((s) => s.score);
  const applyReharmCandidate = useStore((s) => s.applyReharmCandidate);
  // Gated INSIDE the component so every mount site inherits it (#327).
  // Hooks stay unconditional; the flag decides at render below.
  const reharmEnabled = useFlag('reharm');
  // Apply lands beneath a live audition (the store ends it first), so the list
  // is worked out on the harmony underneath and names the layer there. A chord
  // that exists only in the audition has nothing underneath to substitute.
  const target = useMemo(() => dropReharmPreview(score), [score]);
  const present = findChord(target, chordId) !== null;
  const candidates = useMemo(() => suggestForChord(target, chordId), [target, chordId]);
  const layer = activeReharmLayer(target);

  const byRung = useMemo(() => {
    const groups = new Map<number, ReharmCandidate[]>();
    for (const c of candidates) groups.set(c.rung, [...(groups.get(c.rung) ?? []), c]);
    return [...groups.entries()].sort(([a], [b]) => a - b);
  }, [candidates]);

  if (!reharmEnabled || !present) return null;

  return (
    <section
      className="reharm-suggestions"
      data-testid="reharm-suggestions"
      aria-label="Substitutes"
    >
      <header className="reharm-suggestions-head">
        <strong>Substitutes</strong>
        <span className="muted">{layer ? `into ${layer.name}` : 'into a new layer'}</span>
      </header>
      {candidates.length === 0 ? (
        <p className="panel-hint" data-testid="reharm-suggestions-empty">
          Nothing fits the melody here.
        </p>
      ) : (
        byRung.map(([rung, list]) => (
          <div className="reharm-rung" key={rung} data-testid={`reharm-rung-${rung}`}>
            <span className="reharm-rung-name muted">
              {RUNG_NAMES[rung] ?? REHARM_MOVES.find((m) => m.rung === rung)?.name ?? 'More'}
            </span>
            <ul className="reharm-candidates">
              {list.map((c) => (
                <li key={c.id} className="reharm-candidate" data-testid="reharm-candidate">
                  <button
                    type="button"
                    className="btn btn-sm reharm-apply"
                    data-testid="reharm-apply"
                    data-candidate-id={c.id}
                    title={c.rationale}
                    onClick={() => applyReharmCandidate(c)}
                  >
                    {c.label}
                  </button>
                  <span
                    className={`reharm-fit reharm-fit-${c.score.melodyFit}`}
                    title={FIT_LABEL[c.score.melodyFit]}
                    aria-label={FIT_LABEL[c.score.melodyFit]}
                  >
                    {c.score.melodyFit === 'chord-tone'
                      ? '●'
                      : c.score.melodyFit === 'tension'
                        ? '◐'
                        : '○'}
                  </span>
                  <span className="reharm-rationale muted">
                    {c.rationale}
                    {c.changesOnsets
                      ? ` · ${COUNT_WORDS[c.chords.length] ?? c.chords.length} chords`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </section>
  );
}
